import { useCallback, useState } from "react";
import { Tracker } from "@/models/tracker";
import { RunTrackerOptions, runTrackerWithOptions } from "@/api/tracker";
import { useTrackersStore } from "@/hooks";
import useNotif from "@/hooks/use-notif";
import useTranslation from "@/hooks/use-translation";

/**
 * Hook personnalisé pour centraliser les actions communes sur les trackers
 * (exécution, suppression, activation/désactivation)
 */
export const useTrackerActions = () => {
  const { addError, addSuccess } = useNotif();
  const { t } = useTranslation();
  const { remove, update, isDeleting, isUpdating } = useTrackersStore();
  
  // State for the run options modal
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [trackerToRun, setTrackerToRun] = useState<Tracker | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  /**
   * Exécute un tracker manuellement avec options
   */
  const runTracker = useCallback(
    async (trackerId: string, options?: RunTrackerOptions) => {
      setIsRunning(true);
      try {
        await runTrackerWithOptions(trackerId, options);
        addSuccess("Tracker exécuté avec succès");
      } catch (error) {
        addError("Erreur lors de l'exécution du tracker");
        throw error;
      } finally {
        setIsRunning(false);
      }
    },
    [addSuccess, addError]
  );

  /**
   * Ouvre le modal de configuration avant d'exécuter
   */
  const openRunModal = useCallback((tracker: Tracker) => {
    setTrackerToRun(tracker);
    setRunModalOpen(true);
  }, []);

  /**
   * Ferme le modal de configuration
   */
  const closeRunModal = useCallback(() => {
    setRunModalOpen(false);
    setTrackerToRun(null);
  }, []);

  /**
   * Callback quand l'utilisateur confirme l'exécution depuis le modal
   */
  const handleRunConfirm = useCallback(
    async (options: RunTrackerOptions) => {
      if (!trackerToRun) return;
      await runTracker(trackerToRun.id, options);
      closeRunModal();
    },
    [trackerToRun, runTracker, closeRunModal]
  );

  /**
   * Supprime un tracker
   */
  const deleteTracker = useCallback(
    async (trackerId: string) => {
      try {
        await remove(trackerId);
        addSuccess(t("trackerdeleted"));
      } catch (error) {
        addError(t("error500"));
        throw error;
      }
    },
    [remove, addSuccess, addError, t]
  );

  /**
   * Supprime plusieurs trackers
   */
  const deleteTrackers = useCallback(
    async (trackerIds: string[]) => {
      const errors: string[] = [];
      for (const id of trackerIds) {
        try {
          await remove(id);
        } catch {
          errors.push(id);
        }
      }
      if (errors.length > 0) {
        addError(t("n_trackers_delete_failed", { count: errors.length }));
        if (errors.length < trackerIds.length) throw new Error("partial");
      } else {
        const count = trackerIds.length;
        addSuccess(
          count === 1
            ? t("trackerdeleted")
            : `${count} trackers supprimés`,
        );
      }
    },
    [remove, addSuccess, addError, t]
  );

  /**
   * Active ou désactive un tracker
   */
  const toggleTrackerActive = useCallback(
    async (trackerId: string, tracker: Tracker, isActive: boolean) => {
      try {
        await update(trackerId, { status: isActive ? "active" : "inactive" } as Partial<Tracker>);
        addSuccess(`Tracker ${isActive ? "activé" : "désactivé"}`);
      } catch (error) {
        addError("Erreur lors de la mise à jour du tracker");
        throw error;
      }
    },
    [update, addSuccess, addError]
  );

  return {
    runTracker,
    deleteTracker,
    deleteTrackers,
    toggleTrackerActive,
    isRunning,
    isDeleting,
    isUpdating,
    // Run modal state and handlers
    runModalOpen,
    trackerToRun,
    openRunModal,
    closeRunModal,
    handleRunConfirm,
  };
};












